import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TechnicalAnalysisTools = () => {
  const [selectedTool, setSelectedTool] = useState('correlation');

  const tools = [
    { value: 'correlation', label: 'Correlation Matrix', icon: 'Grid3X3' },
    { value: 'volatility', label: 'Volatility Index', icon: 'Activity' },
    { value: 'institutional', label: 'Institutional Flow', icon: 'Building2' }
  ];

  const correlationData = [
    { asset: 'BTC', btc: 1.00, eth: 0.85, bnb: 0.72, ada: 0.68, sol: 0.74, dot: 0.65 },
    { asset: 'ETH', btc: 0.85, eth: 1.00, bnb: 0.78, ada: 0.71, sol: 0.82, dot: 0.69 },
    { asset: 'BNB', btc: 0.72, eth: 0.78, bnb: 1.00, ada: 0.64, sol: 0.69, dot: 0.58 },
    { asset: 'ADA', btc: 0.68, eth: 0.71, bnb: 0.64, ada: 1.00, sol: 0.67, dot: 0.72 },
    { asset: 'SOL', btc: 0.74, eth: 0.82, bnb: 0.69, ada: 0.67, sol: 1.00, dot: 0.63 },
    { asset: 'DOT', btc: 0.65, eth: 0.69, bnb: 0.58, ada: 0.72, sol: 0.63, dot: 1.00 }
  ];

  const volatilityData = [
    { asset: 'Bitcoin', symbol: 'BTC', volatility: 3.2, change: -0.5, trend: 'decreasing' },
    { asset: 'Ethereum', symbol: 'ETH', volatility: 4.1, change: 0.3, trend: 'increasing' },
    { asset: 'BNB', symbol: 'BNB', volatility: 5.8, change: -1.2, trend: 'decreasing' },
    { asset: 'Cardano', symbol: 'ADA', volatility: 6.4, change: 0.8, trend: 'increasing' },
    { asset: 'Solana', symbol: 'SOL', volatility: 7.9, change: 1.5, trend: 'increasing' },
    { asset: 'Polkadot', symbol: 'DOT', volatility: 6.1, change: -0.3, trend: 'decreasing' }
  ];

  const institutionalFlowData = [
    { 
      institution: 'Grayscale', 
      flow: 2100000000, 
      change: 12.5, 
      assets: ['BTC', 'ETH', 'LTC'], 
      trend: 'inflow' 
    },
    { 
      institution: 'MicroStrategy', 
      flow: 850000000, 
      change: 8.3, 
      assets: ['BTC'], 
      trend: 'inflow' 
    },
    { 
      institution: 'Tesla', 
      flow: -120000000, 
      change: -2.1, 
      assets: ['BTC'], 
      trend: 'outflow' 
    },
    { 
      institution: 'Block Inc', 
      flow: 340000000, 
      change: 15.7, 
      assets: ['BTC'], 
      trend: 'inflow' 
    },
    { 
      institution: 'Coinbase Ventures', 
      flow: 680000000, 
      change: 22.4, 
      assets: ['BTC', 'ETH', 'SOL'], 
      trend: 'inflow' 
    },
    { 
      institution: 'Galaxy Digital', 
      flow: 290000000, 
      change: 6.8, 
      assets: ['BTC', 'ETH'], 
      trend: 'inflow' 
    }
  ];

  const getCorrelationColor = (value) => {
    if (value >= 0.8) return 'bg-success text-success-foreground';
    if (value >= 0.6) return 'bg-accent text-accent-foreground';
    if (value >= 0.4) return 'bg-warning text-warning-foreground';
    return 'bg-error text-error-foreground';
  };

  const getVolatilityColor = (volatility) => {
    if (volatility <= 3) return 'text-success';
    if (volatility <= 5) return 'text-accent';
    if (volatility <= 7) return 'text-warning';
    return 'text-error';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing': return 'TrendingUp';
      case 'decreasing': return 'TrendingDown';
      case 'inflow': return 'ArrowDownToLine';
      case 'outflow': return 'ArrowUpFromLine';
      default: return 'Minus';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'increasing': case'inflow': return 'text-success';
      case 'decreasing': case'outflow': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const formatCurrency = (amount) => {
    if (Math.abs(amount) >= 1000000000) {
      return `$${(amount / 1000000000)?.toFixed(1)}B`;
    }
    if (Math.abs(amount) >= 1000000) {
      return `$${(amount / 1000000)?.toFixed(1)}M`;
    }
    return `$${amount?.toLocaleString()}`;
  };

  const renderCorrelationMatrix = () => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr>
            <th className="text-left p-3 text-sm font-medium text-muted-foreground">Asset</th>
            {correlationData?.map((asset) => (
              <th key={asset?.asset} className="text-center p-3 text-sm font-medium text-muted-foreground">
                {asset?.asset}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {correlationData?.map((row) => (
            <tr key={row?.asset} className="border-t border-border">
              <td className="p-3 text-sm font-medium text-card-foreground">{row?.asset}</td>
              <td className="p-3 text-center">
                <span className={`px-2 py-1 text-xs font-medium rounded ${getCorrelationColor(row?.btc)}`}>
                  {row?.btc?.toFixed(2)}
                </span>
              </td>
              <td className="p-3 text-center">
                <span className={`px-2 py-1 text-xs font-medium rounded ${getCorrelationColor(row?.eth)}`}>
                  {row?.eth?.toFixed(2)}
                </span>
              </td>
              <td className="p-3 text-center">
                <span className={`px-2 py-1 text-xs font-medium rounded ${getCorrelationColor(row?.bnb)}`}>
                  {row?.bnb?.toFixed(2)}
                </span>
              </td>
              <td className="p-3 text-center">
                <span className={`px-2 py-1 text-xs font-medium rounded ${getCorrelationColor(row?.ada)}`}>
                  {row?.ada?.toFixed(2)}
                </span>
              </td>
              <td className="p-3 text-center">
                <span className={`px-2 py-1 text-xs font-medium rounded ${getCorrelationColor(row?.sol)}`}>
                  {row?.sol?.toFixed(2)}
                </span>
              </td>
              <td className="p-3 text-center">
                <span className={`px-2 py-1 text-xs font-medium rounded ${getCorrelationColor(row?.dot)}`}>
                  {row?.dot?.toFixed(2)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-center mt-4 space-x-6 text-xs text-muted-foreground">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-success rounded"></div>
          <span>Strong (0.8+)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-accent rounded"></div>
          <span>Moderate (0.6-0.8)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-warning rounded"></div>
          <span>Weak (0.4-0.6)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-error rounded"></div>
          <span>Very Weak (&lt;0.4)</span>
        </div>
      </div>
    </div>
  );

  const renderVolatilityIndex = () => (
    <div className="space-y-4">
      {volatilityData?.map((item) => (
        <div key={item?.symbol} className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <div className="flex items-center space-x-4">
            <div>
              <h4 className="font-medium text-card-foreground">{item?.asset}</h4>
              <p className="text-sm text-muted-foreground">{item?.symbol}</p>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">30-Day Volatility</p>
              <p className={`text-lg font-semibold ${getVolatilityColor(item?.volatility)}`}>
                {item?.volatility}%
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">7-Day Change</p>
              <div className="flex items-center space-x-1">
                <Icon 
                  name={getTrendIcon(item?.trend)} 
                  size={16} 
                  className={getTrendColor(item?.trend)} 
                />
                <span className={`text-sm font-medium ${getTrendColor(item?.trend)}`}>
                  {item?.change > 0 ? '+' : ''}{item?.change}%
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-accent mt-0.5" />
          <div>
            <h5 className="text-sm font-medium text-card-foreground mb-1">Volatility Index Explanation</h5>
            <p className="text-xs text-muted-foreground">
              Volatility measures price fluctuation over time. Lower values indicate more stable assets, 
              while higher values suggest greater price swings and potential risk/reward opportunities.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInstitutionalFlow = () => (
    <div className="space-y-4">
      {institutionalFlowData?.map((institution, index) => (
        <div key={index} className="border border-border rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-medium text-card-foreground">{institution?.institution}</h4>
              <div className="flex flex-wrap gap-1 mt-1">
                {institution?.assets?.map((asset) => (
                  <span key={asset} className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded">
                    {asset}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={getTrendIcon(institution?.trend)} 
                  size={16} 
                  className={getTrendColor(institution?.trend)} 
                />
                <span className={`text-sm font-medium ${getTrendColor(institution?.trend)}`}>
                  {institution?.change > 0 ? '+' : ''}{institution?.change}%
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Net Flow (30 days)</p>
              <p className={`text-lg font-semibold ${institution?.flow > 0 ? 'text-success' : 'text-error'}`}>
                {formatCurrency(institution?.flow)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">
                {institution?.trend === 'inflow' ? 'Money flowing in' : 'Money flowing out'}
              </p>
            </div>
          </div>
        </div>
      ))}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Building2" size={16} className="text-accent mt-0.5" />
          <div>
            <h5 className="text-sm font-medium text-card-foreground mb-1">Institutional Flow Analysis</h5>
            <p className="text-xs text-muted-foreground">
              Tracks capital movements from major institutional investors. Positive flows often indicate 
              institutional confidence and can signal potential price movements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground mb-2">Technical Analysis Tools</h2>
          <p className="text-sm text-muted-foreground">
            Advanced market analysis tools for comprehensive cryptocurrency insights
          </p>
        </div>
        <div className="flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="BarChart3" size={16} className="text-accent" />
          <span>Real-time Data</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
        {tools?.map((tool) => (
          <button
            key={tool?.value}
            onClick={() => setSelectedTool(tool?.value)}
            className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-standard ${
              selectedTool === tool?.value
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tool?.icon} size={16} />
            <span>{tool?.label}</span>
          </button>
        ))}
      </div>
      <div>
        {selectedTool === 'correlation' && renderCorrelationMatrix()}
        {selectedTool === 'volatility' && renderVolatilityIndex()}
        {selectedTool === 'institutional' && renderInstitutionalFlow()}
      </div>
    </div>
  );
};

export default TechnicalAnalysisTools;